/**
    * @description      : Wallet page for hosts to manage earnings and payments
    * @author           : Saif
    * @group            : 
    * @created          : 04/11/2024 - 22:30:43
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 04/11/2024
    * - Author          : Saif
    * - Modification    : 
**/
import { useState, useEffect, useRef } from "react";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWallet,
  faMoneyBill,
  faArrowUp,
  faArrowDown,
  faCreditCard,
  faHistory,
  faShieldAlt,
  faSort,
  faSortUp,
  faSortDown,
  faInfoCircle,
  faDownload
} from "@fortawesome/free-solid-svg-icons";
import { useGet, usePost } from "../../Api";
import { base_url } from "../../Api/apiConfig";
import { readJson } from "../../Api/readJson";
import { Tabs, Tab } from "react-bootstrap";
import CustomPagination from "../../Components/CustomPagination";
import "./style.css";

export const Wallet = () => {
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const historyRef = useRef(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'paid', 'not_paid'
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('payment_date'); // 'paid_amount', 'payment_date', etc.
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [transactionsUrl, setTransactionsUrl] = useState('');
  const [paginationData, setPaginationData] = useState({
    total: 0,
    lastPage: 1,
    perPage: 10,
    currentPage: 1
  });
  
  const { ApiData: WalletStatsData, loading: WalletStatsLoading, error: WalletStatsError, post: GetWalletStats } = usePost(`/wallet-stats`);
  const { ApiData: TransactionsData, loading: TransactionsLoading, error: TransactionsError, get: GetTransactions } = useGet(transactionsUrl);

  // Build API URL with query parameters
  useEffect(() => {
    let url = `/host/wallet/transactions?page=${currentPage}&page_size=${pageSize}`;
    
    // Add status filter
    if (activeTab === 'paid') {
      url += `&status=paid`;
    } else if (activeTab === 'not_paid') {
      url += `&status=not_paid`;
    }
    
    // Add sorting
    if (sortBy) {
      url += `&sort_by=${sortBy}`;
    }
    if (sortOrder) {
      url += `&sort_order=${sortOrder}`;
    }
    
    setTransactionsUrl(url);
  }, [activeTab, currentPage, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    document.title = 'kvr User | Wallet';
    GetWalletStats({}); // Send empty object for POST request
  }, []);

  useEffect(() => {
    if (transactionsUrl) {
      GetTransactions();
    }
  }, [transactionsUrl]);

  useEffect(() => {
    if (WalletStatsData) {
      setWalletData(WalletStatsData);
    }
  }, [WalletStatsData]);

  useEffect(() => {
    if (TransactionsData) {
      // Handle the actual API response structure
      const transactionsList = TransactionsData?.transactions?.data || TransactionsData?.data || [];
      setTransactions(transactionsList);
      
      // Set summary if available
      if (TransactionsData?.summary) {
        setSummary(TransactionsData.summary);
      }
      
      // Update pagination data from transactions object
      if (TransactionsData?.transactions) {
        setPaginationData({
          total: TransactionsData.transactions.total || 0,
          lastPage: TransactionsData.transactions.last_page || 1,
          perPage: TransactionsData.transactions.per_page || pageSize,
          currentPage: TransactionsData.transactions.current_page || currentPage
        });
      } else if (TransactionsData?.pagination) {
        setPaginationData({
          total: TransactionsData.pagination.total || 0,
          lastPage: TransactionsData.pagination.last_page || 1,
          perPage: TransactionsData.pagination.per_page || pageSize,
          currentPage: TransactionsData.pagination.current_page || currentPage
        });
      } else if (Array.isArray(transactionsList)) {
        // Fallback: estimate from data
        setPaginationData({
          total: transactionsList.length,
          lastPage: 1,
          perPage: pageSize,
          currentPage: currentPage
        });
      }
    }
  }, [TransactionsData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset to first page when changing tabs
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (field) => {
    // Map UI field names to API field names
    const fieldMapping = {
      'description': 'description',
      'payment_date': 'payment_date',
      'created_at': 'payment_date', // Fallback mapping
      'amount': 'paid_amount',
      'paid_amount': 'paid_amount'
    };
    
    const apiField = fieldMapping[field] || field;
    
    if (sortBy === apiField) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field with default descending order
      setSortBy(apiField);
      setSortOrder('desc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortIcon = (field) => {
    // Map UI field names to API field names for comparison
    const fieldMapping = {
      'description': 'description',
      'payment_date': 'payment_date',
      'created_at': 'payment_date',
      'amount': 'paid_amount',
      'paid_amount': 'paid_amount'
    };
    
    const apiField = fieldMapping[field] || field;
    
    if (sortBy !== apiField) {
      return <FontAwesomeIcon icon={faSort} className="text-muted" />;
    }
    return sortOrder === 'asc' 
      ? <FontAwesomeIcon icon={faSortUp} className="text-primary" />
      : <FontAwesomeIcon icon={faSortDown} className="text-primary" />;
  };

  // Total Earnings card: jump to the payments Kayifi has made to this host
  const openPaidHistory = () => {
    setActiveTab('paid');
    setCurrentPage(1);
    setTimeout(() => historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const csvCell = (value) => {
    const text = String(value ?? '');
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };

  // Download the transactions of the selected tab (all pages) as a CSV file
  const downloadCsv = async () => {
    setDownloading(true);
    try {
      let url = `${base_url}/host/wallet/transactions?page=1&page_size=1000&sort_by=payment_date&sort_order=desc`;
      if (activeTab === 'paid') url += '&status=paid';
      if (activeTab === 'not_paid') url += '&status=not_paid';

      const response = await fetch(url, {
        headers: { Accept: 'application/json', Authorization: `Bearer ${localStorage.getItem('login')}` },
      });
      const result = await readJson(response);
      const rows = result?.transactions?.data || [];

      const header = ['Payment date', 'Status', 'Description', 'Listing', 'Guest', 'Check-in', 'Check-out',
        'Booking total', 'Kayifi fee', 'Your share', 'Paid to you', 'Paid by', 'Method', 'Transfer ID'];
      const lines = [header].concat(rows.map((t) => [
        t.payment_date ? new Date(t.payment_date).toLocaleString('en-US') : '',
        t.admin_payment_status === 'paid' ? 'Paid' : 'Not paid yet',
        t.description || '',
        t.listing_name || '',
        t.guest_name || '',
        t.check_in || '',
        t.check_out || '',
        t.booking_total ?? '',
        t.kayifi_fee ?? '',
        t.host_share ?? '',
        t.paid_to_host ?? '',
        t.paid_by || '',
        t.payout_method_label || '',
        t.transfer_id || '',
      ]));
      const csv = lines.map((line) => line.map(csvCell).join(',')).join('\n');

      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `kayifi-transactions-${activeTab}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (e) {
      alert(e?.friendly ? e.message : 'Could not download the transactions. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const sharePercent = summary?.host_share_percentage;

  return (
    <>
      <DashboardLayout>
        <div className="wallet-container">
          <div className="row">
            <div className="col-12">
              <h2 className="page-title">
                <FontAwesomeIcon icon={faWallet} className="me-2" />
                Wallet
              </h2>
            </div>
          </div>

          {/* Wallet Stats Cards */}
          <div className="row mb-4">
            <div className="col-md-3 mb-3">
              <div className="wallet-card balance-card">
                <div className="card-icon">
                  <FontAwesomeIcon icon={faMoneyBill} />
                </div>
                <div className="card-content">
                  <h3 className="card-title">Available Balance</h3>
                  <p className="card-amount">
                    {formatCurrency(walletData?.available_balance || 0)}
                  </p>
                  <p className="card-help">Kayifi pays you straight to your payout account, so nothing waits here.</p>
                </div>
              </div>
            </div>

            <div className="col-md-3 mb-3">
              <div className="wallet-card pending-card">
                <div className="card-icon">
                  <FontAwesomeIcon icon={faArrowUp} />
                </div>
                <div className="card-content">
                  <h3 className="card-title">Pending Balance</h3>
                  <p className="card-amount">
                    {formatCurrency(walletData?.pending_balance || 0)}
                  </p>
                  <p className="card-help">Guests have paid for these bookings, Kayifi has not paid you yet.</p>
                </div>
              </div>
            </div>

            <div className="col-md-3 mb-3">
              <div
                className="wallet-card total-card wallet-card-link"
                role="button"
                tabIndex={0}
                title="Show the payments Kayifi made to you"
                onClick={openPaidHistory}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPaidHistory(); } }}
              >
                <div className="card-icon">
                  <FontAwesomeIcon icon={faCreditCard} />
                </div>
                <div className="card-content">
                  <h3 className="card-title">Total Earnings</h3>
                  <p className="card-amount">
                    {formatCurrency(walletData?.total_earnings || 0)}
                  </p>
                  <p className="card-help">Everything Kayifi (admin) has paid to you. Click to see each payment.</p>
                </div>
              </div>
            </div>

            <div className="col-md-3 mb-3">
              <div className="wallet-card security-card">
                <div className="card-icon">
                  <FontAwesomeIcon icon={faShieldAlt} />
                </div>
                <div className="card-content">
                  <h3 className="card-title">Security Deposits</h3>
                  <p className="card-amount">
                    {formatCurrency(walletData?.security_deposits || 0)}
                  </p>
                  <p className="card-help">Guest deposits held for you. Not part of your earnings.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Guests -> Kayifi -> you: where every booking stands */}
          {summary && summary.guest_paid_count !== undefined && (
            <div className="wallet-flow mb-4">
              <div className="wallet-flow-item">
                <span className="wallet-flow-num">{summary.guest_paid_count}</span>
                <span>bookings paid by guests ({formatCurrency(summary.guest_paid_total)})</span>
              </div>
              <div className="wallet-flow-item">
                <span className="wallet-flow-num">{summary.guest_not_paid_count}</span>
                <span>bookings not paid by the guest yet</span>
              </div>
              <div className="wallet-flow-item">
                <span className="wallet-flow-num">{summary.paid_count}</span>
                <span>bookings Kayifi has paid out to you ({formatCurrency(summary.total_received)})</span>
              </div>
              <div className={`wallet-flow-item ${summary.not_released_count ? 'wallet-flow-warn' : ''}`}>
                <span className="wallet-flow-num">{summary.not_released_count}</span>
                <span>guest-paid bookings Kayifi has not released to you yet{summary.not_released_count ? ` (${formatCurrency(summary.not_released_share)} to receive)` : ''}</span>
              </div>
            </div>
          )}

          {/* How it works (plain-language explanation) */}
          <div className="wallet-help mb-4">
            <button type="button" className="wallet-help-toggle" onClick={() => setShowHelp((v) => !v)} aria-expanded={showHelp}>
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              How do my earnings work?
            </button>
            {showHelp && (
              <ol className="wallet-help-list">
                <li>A guest pays Kayifi for the booking. Kayifi keeps its fee{sharePercent ? ` (${(100 - sharePercent).toFixed(0).replace(/\.0+$/, '')}%)` : ''} and your share{sharePercent ? ` (${sharePercent}%)` : ''} is waiting for you. This is shown as <strong>Not paid yet</strong> in the history below.</li>
                <li>Kayifi (admin) then pays your share to you. That payment appears as <strong>Paid</strong>, with the date, who paid and how (for example a Stripe transfer).</li>
                <li><strong>Total Earnings</strong> is simply the total of all those paid payments, so it always matches the <em>Received from Kayifi</em> total in the history.</li>
                <li><strong>Pending</strong> is your share of bookings guests have paid but Kayifi has not paid out yet (the Not paid yet rows below). Kayifi pays you straight to your payout account, so <strong>Available</strong> stays at $0. Security deposits are held for you separately and are not earnings.</li>
              </ol>
            )}
          </div>

          {/* Transaction History */}
          <div className="row">
            <div className="col-12">
              <div className="transaction-section" ref={historyRef}>
                <div className="section-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h3>
                      <FontAwesomeIcon icon={faHistory} className="me-2" />
                      Transaction History
                    </h3>
                    {summary && (
                      <div className="transaction-summary">
                        <span className="summary-item" title="Total of every payment Kayifi has made to you">
                          <strong>Received from Kayifi:</strong> {formatCurrency(summary.total_received ?? summary.total_paid ?? 0)}
                        </span>
                        <span className="summary-item" title="Your share of paid bookings that Kayifi has not paid out yet">
                          <strong>Still to receive:</strong> {formatCurrency(summary.total_owed || 0)}
                        </span>
                        <span className="summary-item">
                          <strong>Payments received:</strong> {summary.paid_count || 0}
                        </span>
                        <span className="summary-item">
                          <strong>Waiting for payment:</strong> {summary.pending_count || 0}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="d-flex justify-content-end mb-2">
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={downloadCsv} disabled={downloading}>
                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                    {downloading ? 'Preparing...' : 'Download CSV'}
                  </button>
                </div>

                {/* Tabs */}
                <Tabs
                  activeKey={activeTab}
                  onSelect={handleTabChange}
                  className="wallet-tabs mb-4"
                  id="transaction-tabs"
                >
                  <Tab eventKey="all" title="All Transactions">
                    <TransactionList 
                      transactions={transactions}
                      loading={TransactionsLoading}
                      formatCurrency={formatCurrency}
                      formatDate={formatDate}
                      formatDateTime={formatDateTime}
                      handleSort={handleSort}
                      getSortIcon={getSortIcon}
                      sortBy={sortBy}
                      sharePercent={sharePercent}
                    />
                  </Tab>
                  <Tab eventKey="paid" title="Paid">
                    <TransactionList 
                      transactions={transactions}
                      loading={TransactionsLoading}
                      formatCurrency={formatCurrency}
                      formatDate={formatDate}
                      formatDateTime={formatDateTime}
                      handleSort={handleSort}
                      getSortIcon={getSortIcon}
                      sortBy={sortBy}
                      sharePercent={sharePercent}
                    />
                  </Tab>
                  <Tab eventKey="not_paid" title="Not Paid">
                    <TransactionList 
                      transactions={transactions}
                      loading={TransactionsLoading}
                      formatCurrency={formatCurrency}
                      formatDate={formatDate}
                      formatDateTime={formatDateTime}
                      handleSort={handleSort}
                      getSortIcon={getSortIcon}
                      sortBy={sortBy}
                      sharePercent={sharePercent}
                    />
                  </Tab>
                </Tabs>

                {/* Pagination */}
                {paginationData.total > 0 && (
                  <CustomPagination
                    itemsPerPage={paginationData.perPage}
                    totalItems={paginationData.total}
                    currentPage={paginationData.currentPage}
                    onPageChange={handlePageChange}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    </>
  );
};

// Transaction List Component
const TransactionList = ({
  transactions,
  loading,
  formatCurrency,
  formatDate,
  formatDateTime,
  handleSort,
  getSortIcon,
  sortBy,
  sharePercent
}) => {
  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">No transactions found</p>
      </div>
    );
  }

  const feePercent = sharePercent ? Number((100 - sharePercent).toFixed(2)) : null;

  return (
    <div className="transaction-table-wrapper">
      <table className="transaction-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('description')} className="sortable">
              <span>DESCRIPTION</span>
              {getSortIcon('description')}
            </th>
            <th onClick={() => handleSort('payment_date')} className="sortable">
              <span>PAYMENT DATE</span>
              {getSortIcon('payment_date')}
            </th>
            <th onClick={() => handleSort('paid_amount')} className="sortable">
              <span>AMOUNT</span>
              {getSortIcon('paid_amount')}
            </th>
            <th>
              <span>STATUS</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((transaction, index) => {
            const isPaid = transaction.admin_payment_status === 'paid';
            const paidToHost = transaction.paid_to_host ?? transaction.paid_amount ?? 0;
            const hasBreakdown = transaction.host_share !== undefined && transaction.host_share !== null;

            return (
              <tr key={transaction?.payment_id || transaction?.booking_id || index}>
                <td>
                  <div className="transaction-description-cell">
                    <div className="transaction-title">
                      {transaction.description || (isPaid ? 'Payment from Kayifi' : 'Booking waiting for payment')}
                    </div>

                    {isPaid && (
                      <div className="transaction-paidby">
                        Paid by <strong>{transaction.paid_by || 'Kayifi (Admin)'}</strong>
                        {transaction.payout_method_label ? ` · ${transaction.payout_method_label}` : ''}
                      </div>
                    )}

                    <div className="transaction-details-list">
                      {transaction.listing_name && (
                        <div className="transaction-detail-item">
                          <strong>Listing:</strong> {transaction.listing_name}
                        </div>
                      )}
                      {transaction.guest_name && (
                        <div className="transaction-detail-item">
                          <strong>Guest:</strong> {transaction.guest_name}
                        </div>
                      )}
                      {transaction.check_in && transaction.check_out && (
                        <div className="transaction-detail-item">
                          <strong>Stay:</strong> {formatDate(transaction.check_in)} - {formatDate(transaction.check_out)}
                        </div>
                      )}
                      {transaction.guest_count && (
                        <div className="transaction-detail-item">
                          <strong>No of Guests:</strong> {transaction.guest_count}
                        </div>
                      )}
                      {transaction.transfer_id && (
                        <div className="transaction-detail-item">
                          <strong>Transfer ID:</strong> {transaction.transfer_id}
                        </div>
                      )}
                    </div>

                    {hasBreakdown && transaction.booking_total ? (
                      <div className="transaction-breakdown">
                        <div><span>Booking total</span><span>{formatCurrency(transaction.booking_total)}</span></div>
                        <div><span>Kayifi fee{feePercent ? ` (${feePercent}%)` : ''}</span><span>- {formatCurrency(transaction.kayifi_fee)}</span></div>
                        <div className="breakdown-total"><span>Your share{sharePercent ? ` (${sharePercent}%)` : ''}</span><span>{formatCurrency(transaction.host_share)}</span></div>
                        {isPaid && Number(transaction.difference_vs_share) !== 0 && (
                          <div className="breakdown-diff"><span>Kayifi paid {Number(transaction.difference_vs_share) > 0 ? 'more' : 'less'} than your share by</span><span>{formatCurrency(Math.abs(transaction.difference_vs_share))}</span></div>
                        )}
                      </div>
                    ) : null}
                    {transaction.duplicate_payment && (
                      <div className="transaction-warning">This booking was paid out more than once.</div>
                    )}
                  </div>
                </td>
                <td>
                  <div className="transaction-date-cell">
                    {isPaid ? formatDateTime(transaction.payment_date) : <span className="text-muted">Not paid yet</span>}
                  </div>
                </td>
                <td>
                  <div className="transaction-amount-cell">
                    {isPaid ? (
                      <>
                        <span className="amount credit">+{formatCurrency(paidToHost)}</span>
                        <small className="amount-note">Received from Kayifi</small>
                      </>
                    ) : (
                      <>
                        <span className="amount due">{formatCurrency(transaction.amount_due)}</span>
                        <small className="amount-note">Kayifi will pay you</small>
                      </>
                    )}
                  </div>
                </td>
                <td>
                  <span className={`transaction-status status-${(transaction?.admin_payment_status || 'na').toLowerCase().replace('_', '-')}`}>
                    {isPaid ? 'Paid' : 'Not paid yet'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
